import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { useTheme } from "../context/ThemeContext";

export default function TermsOfService() {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#050505]' : 'bg-slate-100'} transition-colors duration-300`}>
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/">
            <Button variant="ghost" size="sm" className="rounded-xl">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>

        <div className="glass rounded-2xl p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <FileText className={`w-8 h-8 ${isDarkMode ? 'text-[#00f2ea]' : 'text-cyan-600'}`} />
            <h1 className="text-3xl font-bold">Terms of Service</h1>
          </div>
          
          <p className="opacity-60 mb-8">Last updated: December 21, 2025</p>

          <div className="space-y-8 opacity-90">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Acceptance of Terms</h2>
              <p className="leading-relaxed">
                By accessing or using TheForecastTracker ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service. We reserve the right to modify these Terms at any time, and your continued use constitutes acceptance of any changes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Description of Service</h2>
              <p className="leading-relaxed">
                TheForecastTracker is a weather application that provides weather forecasts, radar imagery, severe weather alerts, and related meteorological information. Our Service includes real-time weather data, extended forecasts, air quality information, and AI-powered weather insights.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. User Accounts</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>You must provide accurate and complete information when creating an account</li>
                <li>You are responsible for maintaining the security of your account credentials</li>
                <li>You must notify us immediately of any unauthorized account access</li>
                <li>One person may not maintain multiple accounts</li>
                <li>You must be at least 13 years old to create an account</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Acceptable Use</h2>
              <p className="mb-2">You agree NOT to:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Use the Service for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Scrape, crawl, or use automated means to access the Service without permission</li>
                <li>Redistribute, resell, or commercially exploit our weather data</li>
                <li>Impersonate any person or entity</li>
                <li>Upload malicious code or content</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Weather Data Disclaimer</h2>
              <p className="leading-relaxed mb-3">
                <strong>IMPORTANT:</strong> Weather forecasts are predictions based on available data and are not guaranteed to be accurate. Weather conditions can change rapidly and unexpectedly.
              </p>
              <ul className="list-disc list-inside space-y-1">
                <li>Do NOT rely solely on our Service for life-safety decisions</li>
                <li>Always follow official government weather warnings and evacuation orders</li>
                <li>We are not liable for any decisions made based on our weather data</li>
                <li>Our Service should be used as one of multiple sources for weather information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Intellectual Property</h2>
              <p className="leading-relaxed">
                All content, features, and functionality of the Service, including but not limited to text, graphics, logos, icons, images, and software, are the exclusive property of TheForecastTracker or our licensors and are protected by copyright, trademark, and other intellectual property laws.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Third-Party Services</h2>
              <p className="leading-relaxed">
                Our Service may integrate with or contain links to third-party services (weather data providers, mapping services, etc.). We are not responsible for the content, privacy policies, or practices of these third parties. Your use of third-party services is at your own risk.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Limitation of Liability</h2>
              <p className="leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, THEFORECASTTRACKER SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF THE SERVICE.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Disclaimer of Warranties</h2>
              <p className="leading-relaxed">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. Termination</h2>
              <p className="leading-relaxed">
                We reserve the right to suspend or terminate your account and access to the Service at our sole discretion, without notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">11. Governing Law</h2>
              <p className="leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Any disputes arising from these Terms shall be resolved in the courts of Tennessee.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">12. Contact Information</h2>
              <p className="leading-relaxed">
                For questions about these Terms, please contact us at:
              </p>
              <div className="mt-3 p-4 rounded-xl bg-white/5">
                <p><strong>TheForecastTracker</strong></p>
                <p>Email: legal@theforecasttracker.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
